import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  Checkroom as CheckroomIcon,
  Style as StyleIcon,
  Collections as CollectionsIcon,
  CalendarMonth as CalendarIcon,
  Flight as FlightIcon,
  Recommend as RecommendIcon,
  Feed as FeedIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
  AutoAwesome as AutoAwesomeIcon,
  Add as AddIcon,
  Logout as LogoutIcon,
  KeyboardCommandKey as CmdIcon,
  Keyboard as KeyboardIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface Command {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  keywords?: string;
  group: 'Navigate' | 'Create' | 'Account';
  run: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const commands = useMemo<Command[]>(() => {
    const go = (path: string) => () => {
      navigate(path);
      onClose();
    };
    return [
      { id: 'nav-dash', label: 'Dashboard', icon: <DashboardIcon />, group: 'Navigate', keywords: 'home stats overview', run: go('/') },
      { id: 'nav-wardrobe', label: 'My Wardrobe', icon: <CheckroomIcon />, group: 'Navigate', keywords: 'closet items clothes', run: go('/wardrobe') },
      { id: 'nav-search', label: 'Search', icon: <SearchIcon />, group: 'Navigate', keywords: 'find query lookup', run: go('/search') },
      { id: 'nav-chat', label: 'AI Stylist Chat', icon: <ChatIcon />, group: 'Navigate', keywords: 'assistant conversation', run: go('/conversations') },
      { id: 'nav-outfits', label: 'Outfits', icon: <StyleIcon />, group: 'Navigate', run: go('/outfits') },
      { id: 'nav-recs', label: 'Recommendations', icon: <RecommendIcon />, group: 'Navigate', keywords: 'suggest', run: go('/recommendations') },
      { id: 'nav-collections', label: 'Collections', icon: <CollectionsIcon />, group: 'Navigate', run: go('/collections') },
      { id: 'nav-calendar', label: 'Calendar', icon: <CalendarIcon />, group: 'Navigate', keywords: 'schedule events', run: go('/calendar') },
      { id: 'nav-trip', label: 'Trip Planner', icon: <FlightIcon />, group: 'Navigate', keywords: 'travel packing', run: go('/trip-planner') },
      { id: 'nav-activity', label: 'Activity Feed', icon: <FeedIcon />, group: 'Navigate', run: go('/activity') },
      { id: 'nav-friends', label: 'Friends', icon: <PeopleIcon />, group: 'Navigate', run: go('/friends') },
      { id: 'nav-profile', label: 'Profile', icon: <PersonIcon />, group: 'Navigate', keywords: 'account settings', run: go('/profile') },
      { id: 'create-item', label: 'Add Item', description: 'Upload a new wardrobe piece', icon: <AddIcon />, group: 'Create', keywords: 'new clothing photo', run: go('/upload') },
      { id: 'create-outfit', label: 'Create Outfit', icon: <StyleIcon />, group: 'Create', keywords: 'build look', run: go('/outfits?action=create') },
      { id: 'create-chat', label: 'Ask the AI Stylist', icon: <AutoAwesomeIcon />, group: 'Create', keywords: 'recommend new conversation', run: go('/conversations') },
      { id: 'create-trip', label: 'Plan a Trip', icon: <FlightIcon />, group: 'Create', keywords: 'travel pack', run: go('/trip-planner') },
      {
        id: 'acc-shortcuts',
        label: 'Keyboard Shortcuts',
        description: 'See every shortcut',
        icon: <KeyboardIcon />,
        group: 'Account',
        keywords: 'help hotkeys keys',
        run: () => {
          onClose();
          window.dispatchEvent(new CustomEvent('wardrobe:open-shortcuts'));
        },
      },
      {
        id: 'acc-logout',
        label: 'Sign Out',
        icon: <LogoutIcon />,
        group: 'Account',
        keywords: 'logout exit',
        run: async () => {
          onClose();
          await logout();
          navigate('/login');
        },
      },
    ];
  }, [navigate, onClose, logout]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(c => {
      const haystack = `${c.label} ${c.description || ''} ${c.keywords || ''} ${c.group}`.toLowerCase();
      return q.split(/\s+/).every(token => haystack.includes(token));
    });
  }, [commands, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmd-idx="${activeIndex}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      filtered[activeIndex]?.run();
    }
  };

  let lastGroup: string | null = null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          mt: '10vh',
          alignSelf: 'flex-start',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <SearchIcon sx={{ color: 'text.secondary', mr: 1.5 }} />
        <InputBase
          inputRef={inputRef}
          fullWidth
          placeholder="Jump to anything…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ fontSize: '1.05rem', fontFamily: 'Lato, sans-serif' }}
        />
        <Chip
          icon={<CmdIcon sx={{ fontSize: 14 }} />}
          label="K"
          size="small"
          sx={{ ml: 1, fontSize: '0.65rem' }}
        />
      </Box>

      <List
        ref={listRef}
        dense
        sx={{
          maxHeight: '50vh',
          overflowY: 'auto',
          py: 0,
          '&::-webkit-scrollbar': { width: 6 },
        }}
      >
        {filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No matches for “{query}”
            </Typography>
          </Box>
        ) : (
          filtered.map((cmd, idx) => {
            const showHeader = cmd.group !== lastGroup;
            lastGroup = cmd.group;
            return (
              <React.Fragment key={cmd.id}>
                {showHeader && (
                  <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                    <Typography variant="overline" color="text.secondary">
                      {cmd.group}
                    </Typography>
                  </Box>
                )}
                <ListItemButton
                  data-cmd-idx={idx}
                  selected={idx === activeIndex}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => cmd.run()}
                  sx={{
                    px: 2,
                    py: 1.25,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(212, 175, 55, 0.12)',
                      borderLeft: '2px solid',
                      borderLeftColor: 'secondary.main',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'text.primary' }}>{cmd.icon}</ListItemIcon>
                  <ListItemText
                    primary={cmd.label}
                    secondary={cmd.description}
                    primaryTypographyProps={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.9rem', fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItemButton>
              </React.Fragment>
            );
          })
        )}
      </List>

      <Divider />
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          px: 2,
          py: 1,
          backgroundColor: 'rgba(0,0,0,0.02)',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          ↑↓ navigate · ↵ select · esc close
        </Typography>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
